import React, { useMemo, useState } from 'react';
import { Badge, Navbar as BSNavbar, Nav, Dropdown } from 'react-bootstrap';

import routes from '../../../routes/constants';
import UserAvatar from '../../../pages/profile/UserAvatar';
import text from '../../../text';
import useUser from '../../hooks/useUser';
import { USER_TYPE, USER_TYPE_TO_DISPLAY_NAME } from '../../../constants/user';
import * as userUtils from '../../../utilities/user';
import * as profileActions from '../../../redux/actions/profile';
import { useDispatch } from 'react-redux';
import { useRedirect } from '../../../hooks/useRedirect';
import { clearUser } from '../../../utilities/storage';
import { isUserAnonymous } from '../../../utilities/user';
import FloatingMenu from '../common/FloatingMenu';
import toUpper from 'lodash/toUpper';
import { alertConstants } from '../../../redux/constants';
import { useUpdateLoginEmailBannerFlagMutation } from '../../../redux/rtk';

const UserNav = () => {
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const currentUser = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [updateLoginEmailBannerFlag] = useUpdateLoginEmailBannerFlagMutation();

  const userBadge = useMemo(
    () => currentUser.pretendType ?? currentUser.actualType,
    [currentUser.actualType, currentUser.pretendType]
  );
  const canShowPretendUserTypes = useMemo(
    () => userUtils.isStaff(currentUser.profile?.role),
    [currentUser.profile?.role]
  );
  const pretendUserTypes = useMemo(
    () =>
      canShowPretendUserTypes
        ? [
            USER_TYPE.FREE,
            USER_TYPE.ESSENTIALS,
            USER_TYPE.LARKI_SUPPORT,
          ].filter((type) => {
            if (currentUser.pretendType && type === currentUser.pretendType)
              return false;
            if (
              !currentUser.pretendType &&
              type === USER_TYPE.LARKI_SUPPORT &&
              userUtils.isStaff(currentUser.profile?.role)
            )
              return false;
            return true;
          })
        : [],
    [
      canShowPretendUserTypes,
      currentUser.pretendType,
      currentUser.profile?.role,
    ]
  );

  if (isUserAnonymous(currentUser.profile)) {
    return null;
  }

  return (
    <Nav className='UserNav'>
      <BSNavbar.Collapse>
        <FloatingMenu
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          placement='bottom-end'
          offset={{ mainAxis: 14, crossAxis: 0 }}
          respectRightMenuSpacing={false}
          arrowConfig={{
            fill: '#ffffff',
            stroke: '#dcdcdc',
            strokeWidth: 1,
            width: 18,
            height: 8,
          }}
          className='user-nav-menu-floating'
          innerClassName='user-nav-menu-floating-inner'
          containerSelector='.user-nav-menu-floating-inner'
          trigger={
            <button
              type='button'
              className='user-nav-trigger'
              aria-label={text('account')}
            >
              <div className='avatar-wrapper'>
                <UserAvatar
                  profileImage={currentUser.profile?.profile_image}
                />
                {userBadge ? (
                  <Badge className={userBadge}>{text(userBadge)}</Badge>
                ) : null}
              </div>
            </button>
          }
        >
          {({ onClose }) => (
            <>
              <div className='p-2 mb-1 user-name-container'>
                <div
                  className='d-flex flex-column user-name-details'
                  onClick={() => {
                    onClose();
                    redirect.push(routes.user.profile);
                  }}
                >
                  <p className='mb-0 font-16 font-weight--600 text-truncate name'>
                    {currentUser.profile?.full_name}
                  </p>
                  <p className='mb-0 font-14 text-truncate email'>
                    {currentUser.profile?.email}
                  </p>
                  <p className='title font-12 font-weight--400'>
                    {toUpper(text('account'))}
                  </p>
                </div>
              </div>

              <Dropdown.Item
                onClick={() => {
                  onClose();
                  redirect.push(routes.dashboard);
                }}
              >
                {text('dashboard')}
              </Dropdown.Item>

              <Dropdown.Item
                href={routes.larkiWebsiteUrl.home}
                target='_blank'
                rel='noreferrer'
                onClick={() => {
                  onClose();
                }}
              >
                {text('larkiURL')}
              </Dropdown.Item>

              {canShowPretendUserTypes ? (
                <>
                  <Dropdown.Divider />

                  {pretendUserTypes.map((type) => (
                    <Dropdown.Item
                      key={type}
                      onClick={() => {
                        onClose();
                        dispatch(profileActions.setPretendType(type));
                      }}
                    >
                      {text('viewAs', {
                        as: USER_TYPE_TO_DISPLAY_NAME[type],
                      })}
                    </Dropdown.Item>
                  ))}

                  <Dropdown.Divider />
                </>
              ) : null}

              <Dropdown.Item
                onClick={async () => {
                  await updateLoginEmailBannerFlag({
                    should_show_email_banner: false,
                  });
                  // clear login email banner
                  dispatch({ type: alertConstants.ALERT_RESET });

                  onClose();
                  // also clear it here in case we're on an external domain
                  clearUser();
                  redirect.push(routes.logout);
                }}
              >
                {text('logout')}
              </Dropdown.Item>
            </>
          )}
        </FloatingMenu>
      </BSNavbar.Collapse>
    </Nav>
  );
};

export default UserNav;
